/* useBrandNetworks
 * ─────────────────────────────────────────────────────────────────
 * Tiny shared hook that fetches `/api/brand-networks` once per page
 * (cached in module scope) and returns:
 *   - brands      : full active brand list
 *   - bySlug      : { [slug]: brand } lookup map
 *   - loading
 *
 * Why module-scope cache instead of React Query: the public brand list
 * changes rarely (admin edits) and a single in-memory map is enough
 * across the few pages that render brand badges. The cache is bypassed
 * by Strict Mode's double mount because we resolve to the same Promise.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cache = null;     // resolved array
let _inflight = null;  // in-flight Promise

const fetchBrandNetworks = () => {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/brand-networks`)
    .then((r) => { _cache = r.data || []; _inflight = null; return _cache; })
    .catch((e) => { _inflight = null; throw e; });
  return _inflight;
};

// Exposed escape-hatch the admin tabs can call after a brand mutation.
export const invalidateBrandNetworks = () => { _cache = null; _inflight = null; };

const useBrandNetworks = () => {
  const [brands, setBrands] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let cancelled = false;
    if (_cache) { setBrands(_cache); setLoading(false); return undefined; }
    fetchBrandNetworks()
      .then((list) => { if (!cancelled) { setBrands(list); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const bySlug = brands.reduce((acc, b) => { acc[b.slug] = b; return acc; }, {});
  return { brands, bySlug, loading };
};

export default useBrandNetworks;
